import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, platformAPI } from '../services/api';
import { Eye, EyeOff, AlertTriangle, Sparkles, Search, Star, PartyPopper, CreditCard, Clock, DollarSign, Shield, Award } from 'lucide-react';
import { getRecaptchaToken } from '../utils/recaptcha';
import PasswordStrength from '../components/PasswordStrength';
import './Auth.css';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    user_type: 'homeowner',
    first_name: '',
    last_name: '',
    phone: '',
    city: '',
    state: '',
    zip_code: '',
    // Homeowner-specific fields
    is_hoa_home: false, // Checkbox for HOA homeowner
    // Contractor-specific fields
    promo_code: '', // Optional promo code for free lifetime access
    referred_by: '', // Referral code - who referred this person
    business_name: '',
    license_number: '',
    license_state: '',
    insurance_company: '',
    insurance_policy_number: '',
    opt_in_founding_member: false, // Founding member opt-in
    // Business-specific fields
    business_type: '', // restaurant, retail, property_management, etc.
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [foundingMemberInfo, setFoundingMemberInfo] = useState(null);
  const [checkingState, setCheckingState] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [betaMode, setBetaMode] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => { document.title = 'Sign Up | Real Time Repairs'; }, []);

  // Region mapping for location-specific messaging
  const regionNames = {
    'berks-county': 'Eastern Berks County, PA',
    'mohnton': 'Mohnton & Eastern Berks County, PA',
    'reading': 'Reading & Berks County, PA',
    'defuniak': 'DeFuniak Springs, FL',
    'nw-florida': 'Northwest Florida',
    'pensacola': 'Pensacola, FL',
    'panama-city': 'Panama City, FL',
  };

  const [regionName, setRegionName] = useState(null);

  // Check for user type, referral code, and region in URL
  useEffect(() => {
    // Handle ?type=contractor or ?type=homeowner
    const userType = searchParams.get('type');
    if (userType && ['homeowner', 'contractor', 'business'].includes(userType)) {
      setFormData(prev => ({
        ...prev,
        user_type: userType
      }));
    }

    // Handle ?ref=CODE for referral
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({
        ...prev,
        referred_by: refCode.toUpperCase()
      }));
    }

    // Handle ?region=berks-county for location-specific messaging
    const region = searchParams.get('region');
    if (region && regionNames[region]) {
      setRegionName(regionNames[region]);
    }
  }, [searchParams]);

  // Fetch beta mode status on mount
  useEffect(() => {
    const fetchBetaStatus = async () => {
      try {
        // M5 fix: Use platformAPI instead of direct axios call
        const response = await platformAPI.getBetaStatus();
        setBetaMode(response.data.beta_mode);
      } catch (error) {
        // Default to false if error
        setBetaMode(false);
      }
    };
    fetchBetaStatus();
  }, []);

  // Check founding member availability when state changes
  useEffect(() => {
    const checkFoundingMember = async () => {
      if (formData.state && formData.state.length === 2 && formData.user_type === 'contractor') {
        setCheckingState(true);
        try {
          // M5 fix: Use authAPI instead of direct axios call
          const response = await authAPI.checkFoundingMember(formData.state);
          setFoundingMemberInfo(response.data);
        } catch (error) {
          setFoundingMemberInfo(null);
        } finally {
          setCheckingState(false);
        }
      } else {
        setFoundingMemberInfo(null);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(checkFoundingMember, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.state, formData.user_type]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // Real-time password validation
    if (name === 'password') {
      if (value.length > 0 && (value.length < 8 || !/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/[0-9]/.test(value))) {
        setPasswordError('Password must be at least 8 characters with uppercase, lowercase, and a number');
      } else {
        setPasswordError('');
      }

      // Check confirm password match if it's already filled
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    }

    // Real-time confirm password validation
    if (name === 'confirmPassword') {
      if (value && value !== formData.password) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to continue');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setPasswordError('Password must be at least 8 characters with uppercase, lowercase, and a number');
      return;
    }

    if (formData.state.length !== 2) {
      setError('State must be 2 letters (e.g., GA, NY, CA)');
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token
      const recaptcha_token = await getRecaptchaToken('signup');

      // Remove confirmPassword before sending to API
      const { confirmPassword, ...userData } = formData;

      const result = await register({ ...userData, recaptcha_token });

      if (result.success) {
        // GA4: Track successful signup
        if (window.gtag) {
          window.gtag('event', 'sign_up', { method: 'email', user_type: userData.user_type || 'homeowner' });
        }
        // Use window.location for a full page reload to ensure clean state
        window.location.href = '/dashboard';
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card register-card">
        <div className="auth-logo">
          <img src="/logo-circle.webp" alt="Real Time Repairs" className="auth-logo-image" width="48" height="48" />
          <span className="auth-logo-text">Real Time <span className="auth-logo-accent">Repairs</span></span>
        </div>
        <h1 className="auth-title">
          {formData.user_type === 'contractor'
            ? 'Get Early Access to Local Jobs'
            : 'Create Your Account'}
        </h1>
        <p className="auth-subtitle">
          {formData.user_type === 'homeowner'
            ? 'Something broke? Post the job once and local contractors come to you.'
            : formData.user_type === 'business'
            ? 'Free for HOAs - Monitor compliance and manage your community'
            : 'Free to join. Start bidding immediately. No credit card needed.'}
        </p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="user_type">I am a...</label>
            <select
              id="user_type"
              name="user_type"
              value={formData.user_type}
              onChange={handleChange}
              required
            >
              <option value="homeowner">Homeowner</option>
              <option value="contractor">Contractor</option>
              {/* HOA / Property Manager option hidden until HOA features are complete */}
            </select>
          </div>

          {/* Why Real Time Repairs - Contractor Value Proposition */}
          {formData.user_type === 'contractor' && (
            <div className="value-proposition-box">
              <h3 className="value-proposition-title">How it works</h3>
              <div className="value-proposition-grid">
                <div className="value-item">
                  <Search size={20} color="#16A34A" />
                  <div>
                    <strong>Browse local homeowner jobs</strong>
                    <p>See real jobs from homeowners in your area. Filter by trade, location, and budget.</p>
                  </div>
                </div>
                <div className="value-item">
                  <Clock size={20} color="#16A34A" />
                  <div>
                    <strong>Submit bids directly</strong>
                    <p>Bid on jobs you want. No phone tag, no runaround. Homeowners see your proposal directly.</p>
                  </div>
                </div>
                <div className="value-item">
                  <Shield size={20} color="#16A34A" />
                  <div>
                    <strong>3 free won jobs, no credit card</strong>
                    <p>Win your first 3 jobs at no cost. Only pay $50/mo after that.</p>
                  </div>
                </div>
                <div className="value-item">
                  <DollarSign size={20} color="#16A34A" />
                  <div>
                    <strong>No per-lead fees ever</strong>
                    <p>Just a flat $50/mo after your free jobs. No commission, no surprise charges.</p>
                  </div>
                </div>
              </div>
              {regionName && (
                <p style={{fontSize: '0.85rem', color: '#666', marginTop: '1rem', textAlign: 'center'}}>
                  We're starting in {regionName} and onboarding a small group of local contractors first.
                </p>
              )}
            </div>
          )}

          {/* Pricing Info */}
          {formData.user_type === 'homeowner' && (
            <div className="pricing-info free">
              <div className="pricing-info-icon"><Sparkles size={24} color="#F97316" /></div>
              <div>
                <strong>Always Free</strong>
                <p>Homeowners can use all features at no cost!</p>
              </div>
            </div>
          )}

          {formData.user_type === 'contractor' && (
            <div className="pricing-info free">
              <div className="pricing-info-icon"><Sparkles size={24} color="#16A34A" /></div>
              <div>
                <strong>Free to Start</strong>
                <p>Win your first 3 jobs at no cost. No credit card required. After 3 won jobs, continue for $50/mo.</p>
              </div>
            </div>
          )}

          {/* HOA/Business Account section hidden until HOA features are complete */}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder="John"
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="john.doe@example.com"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
              autoComplete="tel"
              inputMode="tel"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                placeholder="Atlanta"
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State / Province</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                placeholder="CA / ON"
                maxLength="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="zip_code">ZIP / Postal Code</label>
              <input
                type="text"
                id="zip_code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                required
                placeholder="12345 or M9A 0A3"
              />
            </div>
          </div>

          {/* Contractor-specific fields */}
          {formData.user_type === 'contractor' && (
            <>
              <div className="contractor-section">
                <h3>Contractor Information (Optional)</h3>
                <p className="section-subtitle">You can add license and insurance details now or later in your profile. Required before bidding on jobs.</p>
              </div>

              <div className="form-group">
                <label htmlFor="business_name">Business Name</label>
                <input
                  type="text"
                  id="business_name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  placeholder="Your Company LLC"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="license_number">License Number</label>
                  <input
                    type="text"
                    id="license_number"
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleChange}
                    placeholder="License #"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="license_state">License State / Province</label>
                  <input
                    type="text"
                    id="license_state"
                    name="license_state"
                    value={formData.license_state}
                    onChange={handleChange}
                    placeholder="CA / ON (defaults to yours)"
                    maxLength="2"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="insurance_company">Insurance Company</label>
                <input
                  type="text"
                  id="insurance_company"
                  name="insurance_company"
                  value={formData.insurance_company}
                  onChange={handleChange}
                  placeholder="Insurance provider name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="insurance_policy_number">Insurance Policy Number</label>
                <input
                  type="text"
                  id="insurance_policy_number"
                  name="insurance_policy_number"
                  value={formData.insurance_policy_number}
                  onChange={handleChange}
                  placeholder="Policy #"
                />
              </div>
            </>
          )}

          {/* Promo code only for contractors */}
          {formData.user_type === 'contractor' && (
            <div className="form-group">
              <label htmlFor="promo_code">
                Promo Code <span style={{fontWeight: 'normal', color: '#666'}}>(Optional)</span>
              </label>
              <input
                type="text"
                id="promo_code"
                name="promo_code"
                value={formData.promo_code || ''}
                onChange={handleChange}
                placeholder="Enter promo code if you have one"
                style={{textTransform: 'uppercase'}}
              />
              <small style={{display: 'block', marginTop: '0.5rem', color: '#666'}}>
                Enter your code to apply any available discounts
              </small>
            </div>
          )}

          {/* Referral code - who referred you */}
          <div className="form-group">
            <label htmlFor="referred_by">
              Referral Code <span style={{fontWeight: 'normal', color: '#666'}}>(Optional)</span>
            </label>
            <input
              type="text"
              id="referred_by"
              name="referred_by"
              value={formData.referred_by || ''}
              onChange={handleChange}
              placeholder="Were you referred by someone?"
              style={{textTransform: 'uppercase'}}
            />
            {formData.referred_by && (
              <small style={{display: 'block', marginTop: '0.5rem', color: '#16a34a'}}>
                Referral code applied: {formData.referred_by}
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Min. 8 characters"
                className={passwordError ? 'input-error' : ''}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {passwordError && (
              <div className="field-error" style={{
                color: '#dc2626',
                fontSize: '0.875rem',
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <AlertTriangle size={14} />
                <span>{passwordError}</span>
              </div>
            )}
            <PasswordStrength password={formData.password} />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Re-enter password"
                className={confirmPasswordError ? 'input-error' : ''}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {confirmPasswordError && (
              <div className="field-error" style={{
                color: '#dc2626',
                fontSize: '0.875rem',
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <AlertTriangle size={14} />
                <span>{confirmPasswordError}</span>
              </div>
            )}
          </div>

          <div className="form-group terms-agreement">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                required
              />
              <span>
                I agree to the{' '}
                <Link to="/terms-of-service" target="_blank" className="terms-link">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/privacy-policy" target="_blank" className="terms-link">
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? 'Creating account...' : formData.user_type === 'contractor' ? 'Get Early Access' : 'Sign Up Free'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Log in
            </Link>
          </p>
          <p style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            New to RealTime Repairs?{' '}
            <a href="https://www.realtimerepairs.com" className="auth-link" target="_blank" rel="noopener noreferrer">
              Learn More
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
